/* Sunday delight layer — the page follows the sun, literally.
 * Each touch activates only when its root element exists, so options can be
 * removed by deleting their markup alone.
 *   A  [data-sun-now]  live London sun ticker (footer)
 *   D  always          time-aware favicon + console greeting
 */
(function () {
  'use strict';

  /* ---------- Solar maths (NOAA approximation, good to ~0.1°) ---------- */

  var LONDON = { lat: 51.5074, lon: -0.1278 };
  var RAD = Math.PI / 180;

  // Solar elevation in degrees at a given Date (UTC-based, location fixed).
  function solarElevation(date) {
    var start = Date.UTC(date.getUTCFullYear(), 0, 0);
    var day = Math.floor((date.getTime() - start) / 864e5);
    var minutes = date.getUTCHours() * 60 + date.getUTCMinutes() + date.getUTCSeconds() / 60;
    var g = ((2 * Math.PI) / 365) * (day - 1 + (minutes / 60 - 12) / 24);
    var eqtime =
      229.18 *
      (0.000075 +
        0.001868 * Math.cos(g) -
        0.032077 * Math.sin(g) -
        0.014615 * Math.cos(2 * g) -
        0.040849 * Math.sin(2 * g));
    var decl =
      0.006918 -
      0.399912 * Math.cos(g) +
      0.070257 * Math.sin(g) -
      0.006758 * Math.cos(2 * g) +
      0.000907 * Math.sin(2 * g) -
      0.002697 * Math.cos(3 * g) +
      0.00148 * Math.sin(3 * g);
    var tst = minutes + eqtime + 4 * LONDON.lon;
    var ha = (tst / 4 - 180) * RAD;
    var lat = LONDON.lat * RAD;
    var elev = Math.asin(Math.sin(lat) * Math.sin(decl) + Math.cos(lat) * Math.cos(decl) * Math.cos(ha));
    return elev / RAD;
  }

  // Scan today (UTC) minute-by-minute for the horizon crossings.
  // Official sunrise/sunset is the centre crossing -0.833° (refraction
  // plus the solar radius), not geometric 0°.
  var HORIZON = -0.833;
  function sunEvents(now) {
    var base = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    var rise = null;
    var set = null;
    var prev = solarElevation(new Date(base));
    for (var m = 1; m <= 1440; m++) {
      var elev = solarElevation(new Date(base + m * 6e4));
      if (prev <= HORIZON && elev > HORIZON) rise = new Date(base + m * 6e4);
      if (prev > HORIZON && elev <= HORIZON) set = new Date(base + m * 6e4);
      prev = elev;
    }
    return { rise: rise, set: set };
  }

  // Map elevation to the SL1's colour-temperature arc (2650 K dusk amber
  // to 6000 K noon white).
  function cctForElevation(elev) {
    if (elev <= -4) return 2650;
    var t = Math.min(1, (elev + 4) / 46);
    var s = t * t * (3 - 2 * t);
    return Math.round(2650 + s * (6000 - 2650));
  }

  // Colour temperature to RGB (Tanner Helland approximation).
  function cctToRgb(kelvin) {
    var t = kelvin / 100;
    var r, g, b;
    if (t <= 66) {
      r = 255;
      g = 99.47 * Math.log(t) - 161.12;
      b = t <= 19 ? 0 : 138.52 * Math.log(t - 10) - 305.04;
    } else {
      r = 329.7 * Math.pow(t - 60, -0.1332);
      g = 288.12 * Math.pow(t - 60, -0.0755);
      b = 255;
    }
    var clamp = function (v) {
      return Math.max(0, Math.min(255, Math.round(v)));
    };
    return [clamp(r), clamp(g), clamp(b)];
  }

  function londonTime(date) {
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/London',
    }).format(date);
  }

  /* ---------- A — The sun, right now ---------- */

  function initSunNow(root) {
    var disc = root.querySelector('[data-sun-now-disc]');
    var main = root.querySelector('[data-sun-now-main]');
    var meta = root.querySelector('[data-sun-now-meta]');
    if (!disc || !main || !meta) return;

    function describe(elev, rising, cct) {
      if (elev <= -6) return 'The sun is down over London. Sunday holds a candlelit 2,650 K until morning.';
      if (elev <= 0 && rising) return 'First light over London. Sunday is warming from amber into daylight.';
      if (elev <= 0) return 'Dusk over London. Sunday has settled to 2,650 K, the colour of candlelight.';
      if (elev <= 8 && rising) return 'Golden hour over London. Sunday is climbing from amber to morning white.';
      if (elev <= 8) return 'Golden hour over London. Sunday is easing back down to amber.';
      return 'Full daylight over London. Sunday is running at ' + cct.toLocaleString('en-GB') + ' K to match.';
    }

    function update() {
      var now = new Date();
      var elev = solarElevation(now);
      var rising = solarElevation(new Date(now.getTime() + 6e5)) > elev;
      var cct = cctForElevation(elev);
      var rgb = cctToRgb(cct);
      var colour = 'rgb(' + rgb.join(',') + ')';

      disc.style.background = colour;
      disc.style.boxShadow = '0 0 14px 3px rgba(' + rgb.join(',') + ', 0.55)';

      main.textContent = describe(elev, rising, cct);

      var events = sunEvents(now);
      var bits = [londonTime(now) + ' in London'];
      if (events.rise) bits.push('Sunrise ' + londonTime(events.rise));
      if (events.set) bits.push('Sunset ' + londonTime(events.set));
      meta.textContent = bits.join(' · ');
    }

    update();
    setInterval(update, 6e4);
  }

  /* ---------- D — Ignition extras: favicon + console ---------- */

  function initFavicon() {
    function icon(stops) {
      var svg =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">' +
        '<defs><radialGradient id="g" cx="50%" cy="42%" r="62%">' +
        stops +
        '</radialGradient></defs>' +
        '<circle cx="32" cy="32" r="28" fill="url(#g)"/></svg>';
      return 'data:image/svg+xml,' + encodeURIComponent(svg);
    }
    var day = icon(
      '<stop offset="0" stop-color="#FFFDF7"/><stop offset=".55" stop-color="#FFEFC9"/><stop offset="1" stop-color="#8FB8DD"/>'
    );
    var dusk = icon(
      '<stop offset="0" stop-color="#FFE3B0"/><stop offset=".5" stop-color="#F9A045"/><stop offset="1" stop-color="#D64E0A"/>'
    );
    var hour = new Date().getHours();
    var href = hour >= 7 && hour < 19 ? day : dusk;

    var link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.type = 'image/svg+xml';
    link.href = href;
  }

  function initConsole() {
    try {
      console.log(
        '%cSunday Light%c☀ Indoor sunlight.\n' +
          '34,500 lumens · CRI 97+ · 2,650–6,000 K · handmade in London.\n' +
          'The light follows the sun. So does this page.\n' +
          'enquiries@sundaylight.cc',
        'font-family: Georgia, serif; font-size: 18px; color: #F77E2D; padding: 4px 0;',
        'font-size: 12px; color: #6B655B; line-height: 1.6;'
      );
    } catch (e) {
      /* consoles are optional */
    }
  }

  /* ---------- boot ---------- */

  function boot() {
    var sunNow = document.querySelector('[data-sun-now]');
    if (sunNow) initSunNow(sunNow);

    initFavicon();
    initConsole();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
